export interface NotificationTypeInfo {
  id?: string | null;
}

export interface NotificationLike {
  title?: string | null;
  description?: string | null;
  url?: string | null;
  externalId?: string | null;
  type?: NotificationTypeInfo | null;
}

export type NotificationCategory = 'client-request' | 'vault';

const LEAD_OR_TASK = /\blead\b|\btask\b/i;
const CLIENT_REQUEST = /client[\s_-]?request/i;
const VAULT = /\bvault\b|\bfile\b|\bdocument\b/i;

/**
 * `AccountNotification` has no category field — the backend also emits notifications for
 * modules the new CRM doesn't have (leads, tasks), which DEV-3856 asks to exclude. Mirrors the
 * sibling CRM's string-matching notification router: `externalId` ("EntityType:id") first since
 * it's the most structured signal, then `type.id`, then free-text title/description/url.
 * Returns `null` (hidden) for anything that isn't recognizably a Client Request or Vault event.
 */
export function categorizeNotification(n: NotificationLike): NotificationCategory | null {
  const externalEntity = /^([a-z_-]+):/i.exec(n.externalId ?? '')?.[1] ?? '';
  if (CLIENT_REQUEST.test(externalEntity)) return 'client-request';
  if (/^(vault|file)$/i.test(externalEntity)) return 'vault';

  // Type ids are typically SCREAMING_SNAKE_CASE (e.g. "VAULT_FILE_SHARED") — underscores are
  // word characters in regex, so `\bvault\b` wouldn't match inside them without normalizing.
  const typeId = (n.type?.id ?? '').replace(/[_-]/g, ' ');
  if (CLIENT_REQUEST.test(typeId)) return 'client-request';
  if (VAULT.test(typeId)) return 'vault';
  if (LEAD_OR_TASK.test(typeId)) return null;

  const url = n.url ?? '';
  if (/\/client-requests/i.test(url)) return 'client-request';
  if (/\/vault/i.test(url)) return 'vault';

  const text = `${n.title ?? ''} ${n.description ?? ''}`;
  if (LEAD_OR_TASK.test(text)) return null;
  if (CLIENT_REQUEST.test(text)) return 'client-request';
  if (VAULT.test(text)) return 'vault';

  return null;
}
